import { Map as OlMap } from "ol"

// `renderFrame_` is private on OpenLayers' map class. Intersecting OlMap with
// a public property of the same name collapses the type to `never`.
type MapPrototypeWithRenderFrame = {
  renderFrame_?: (this: OlMap, time: number) => void
}

let installed = false

function isMissingWebglContextError(err: unknown): boolean {
  if (!(err instanceof TypeError)) return false
  const message = err.message || ""
  return (
    message.includes("createTexture") ||
    message.includes("createFramebuffer") ||
    message.includes("createBuffer") ||
    message.includes("createShader")
  )
}

/**
 * OpenLayers 6 WebGLTileLayer assumes `canvas.getContext('webgl')` always
 * succeeds. When the context is unavailable (GPU limit, context-lost after
 * hiding the map with `d-none`, driver reset) Helper constructs
 * PostProcessingPass with `gl === null` and crashes on `gl.createTexture()`.
 *
 * Swallow that specific frame error so the rest of the app keeps running;
 * the next successful context will render normally.
 */
export function installWebglRenderGuard(): void {
  if (installed) return
  installed = true

  const proto = OlMap.prototype as unknown as MapPrototypeWithRenderFrame
  const original = proto.renderFrame_
  if (typeof original !== "function") return

  proto.renderFrame_ = function (this: OlMap, time: number) {
    try {
      original.call(this, time)
    } catch (err) {
      if (isMissingWebglContextError(err)) {
        return
      }
      throw err
    }
  }
}
